// Import a TESTIFY exam into GRADIFY (brief §13.5): store the TestifyExam and one
// LOCKED Quiz per compatible subject, with the hashed answer key. Parallel to the
// manual Create Quiz flow; the only difference is where the key came from.
import { PRESETS, type Preset } from '../db/schema.js';
import type { Repositories } from '../db/repositories.js';
import { newId } from '../db/repositories.js';
import type { TestifyValidateResponse, TestifyExportResponse, TestifyExportSubject, TestifyCustomField } from './types.js';

const OPTIONS = ['A', 'B', 'C', 'D'];

export interface ImportedSubject {
  quizId: string;
  title: string;
  items: number;
  /** competencies auto-mapped from the exam's competency_code fields (0 = none sent). */
  competencies: number;
}
export interface IncompatibleSubject {
  name: string;
  items: number;
  reason: string;
}
/** One subject the exam offers, as the picker shows it. */
export interface SubjectOption {
  testId: string;
  title: string;
  items: number;
  competencies: number;
  importable: boolean;
  /** set only when importable is false. */
  reason?: string;
}
export interface ImportResult {
  examId: string;
  /** the exam's name in TESTIFY, for the "imported from …" summary. */
  examTitle: string;
  examCode: string;
  schoolName: string;
  created: ImportedSubject[];
  incompatible: IncompatibleSubject[]; // not imported: wrong item count, or not eligible in TESTIFY
  /** titles the teacher chose not to import — offered by the exam, not by this school. */
  notSelected: string[];
  requiredCustomFields: TestifyCustomField[];
}

/**
 * Map a subject's questions/choices to a GRADIFY hashed key. Choice order_no is
 * canonical (§13.3): order_no 1 → 'A', 2 → 'B', … — the exact same fixed order the
 * printed sheet uses, so a scanned letter maps straight to the right key_hash.
 */
function hashedKeyFor(subject: TestifyExportSubject): {
  items: number;
  hashedAnswers: Record<number, Record<string, string>>;
  points: Record<number, number>;
  uploadMap: Record<number, { quesId: number; choiceIds: Record<string, number> }>;
  /** learning competency → the items measuring it, in item order. The wire field is
   *  still named `competency_code`, but the new curriculum has no codes and TESTIFY
   *  keys on the Learning Competency Description, so its value is taken as the
   *  competency itself. Grouping questions by it IS a Table of Specifications. */
  competencyItems: Map<string, number[]>;
} {
  const hashedAnswers: Record<number, Record<string, string>> = {};
  const points: Record<number, number> = {};
  const uploadMap: Record<number, { quesId: number; choiceIds: Record<string, number> }> = {};
  const competencyItems = new Map<string, number[]>();
  const questions = [...subject.questions].sort((a, b) => a.order_no - b.order_no);
  questions.forEach((question, qi) => {
    const q = question.order_no || qi + 1;
    const row: Record<string, string> = {};
    const choiceIds: Record<string, number> = {};
    [...question.choices]
      .sort((a, b) => a.order_no - b.order_no)
      .forEach((choice, ci) => {
        // TESTIFY supplies the letter directly; fall back to canonical order_no→A/B/C/D.
        const letter = choice.letter || OPTIONS[choice.order_no ? choice.order_no - 1 : ci];
        // key_hash is integer 0 when wrong, hex string when correct — normalise to a
        // string so scoring's zero/non-zero check ('0' vs hash) works either way (§13.3).
        if (letter) row[letter] = String(choice.key_hash);
        // choice_id is what /upload's selectedChoiceId carries (§13.8).
        if (letter) choiceIds[letter] = choice.choice_id;
      });
    hashedAnswers[q] = row;
    points[q] = question.points ?? 1;
    uploadMap[q] = { quesId: question.ques_id, choiceIds };
    const competency = (question.competency_code ?? '').trim();
    if (competency) {
      const list = competencyItems.get(competency);
      if (list) list.push(q);
      else competencyItems.set(competency, [q]);
    }
  });
  return { items: questions.length, hashedAnswers, points, uploadMap, competencyItems };
}

/**
 * Why a subject cannot be imported, or null when it can. ONE source of truth,
 * read by both the subject picker and the import itself, so the list a teacher
 * ticks can never disagree with what actually gets created.
 */
function incompatibleReason(subject: TestifyExportSubject, items: number): string | null {
  if (subject.eligible === false) return subject.reasons?.join('; ') || 'not eligible in TESTIFY';
  // The printed sheets and their layout maps ship as a fixed library, so an item
  // count with no preset has no sheet to scan (§10 — no in-app layout generation).
  if (!(PRESETS as readonly number[]).includes(items)) return `no ${items}-item sheet preset`;
  return null;
}

/**
 * What this exam offers, for the picker. A unified exam can carry a hundred
 * subjects while a school offers a handful, so the teacher chooses; this reports
 * every subject with its item count and, where it can't be imported, why.
 */
export function subjectOptions(exportResp: TestifyExportResponse): SubjectOption[] {
  return exportResp.subjects.map((subject) => {
    const { items, competencyItems } = hashedKeyFor(subject);
    const reason = incompatibleReason(subject, items);
    return {
      testId: String(subject.test_id),
      title: subject.title,
      items,
      competencies: competencyItems.size,
      importable: reason === null,
      reason: reason ?? undefined,
    };
  });
}

export async function applyExportedExam(
  repos: Repositories,
  examCode: string,
  token: string,
  validate: TestifyValidateResponse,
  exportResp: TestifyExportResponse,
  opts: { selectedTestIds?: readonly string[] } = {},
): Promise<ImportResult> {
  const examId = newId();
  const customFields: TestifyCustomField[] = (exportResp.custom_fields ?? []).map((f) => ({
    key: f.key,
    label: f.label,
    required: !!f.required,
  }));
  await repos.addTestifyExam({
    id: examId,
    examCode,
    examTitle: exportResp.exam.title ?? validate.exam.title ?? '',
    gradeLevel: exportResp.exam.grade_level ?? '',
    schoolYear: exportResp.exam.academic_year ?? '',
    token,
    schoolId: String(exportResp.school.school_id),
    schoolCode: String(exportResp.school.school_code ?? ''),
    schoolName: exportResp.school.school_name,
    compileVersion: String(exportResp.exam.compile_version),
    compileVersionNum: Number(exportResp.exam.compile_version),
    signingKey: exportResp.signing?.key ?? '',
    requiredCustomFields: customFields,
    validatedAt: Date.now(),
  });

  const created: ImportedSubject[] = [];
  const incompatible: IncompatibleSubject[] = [];
  const notSelected: string[] = [];
  const grade = exportResp.exam.grade_level ?? '';
  // No selection given = import everything importable (the demo path and the
  // pre-picker behaviour). An empty array is a real, honoured choice: nothing.
  const selected = opts.selectedTestIds ? new Set(opts.selectedTestIds.map(String)) : null;

  for (const subject of exportResp.subjects) {
    const { items, hashedAnswers, points, uploadMap, competencyItems } = hashedKeyFor(subject);
    const reason = incompatibleReason(subject, items);
    if (reason) {
      incompatible.push({ name: subject.title, items, reason });
      continue;
    }
    // A subject the teacher left unticked is NOT incompatible — this school just
    // doesn't offer it. Kept apart so the summary never calls it a problem.
    if (selected && !selected.has(String(subject.test_id))) {
      notSelected.push(subject.title);
      continue;
    }
    const { quiz } = await repos.createQuiz(
      {
        title: subject.title,
        subject: subject.title,
        gradeLevel: grade,
        items: items as Preset,
        origin: 'testify',
        testifyExamId: examId,
        testId: String(subject.test_id),
      },
      [], // classes are assigned afterwards, like a local quiz
    );
    // Locked hashed key — no plain letters stored, never editable (§13.3).
    await repos.setAnswerKey(quiz.id, {}, points, { hashedAnswers, locked: true, uploadMap });
    // TESTIFY tags each question with its learning competency, which IS an
    // item→competency map — so an imported exam gets Item/Competency Analysis with
    // no TOS to enter. The teacher can still edit the wording afterwards.
    if (competencyItems.size) {
      await repos.setCompetencyMap(
        quiz.id,
        [...competencyItems.entries()].map(([description, itemNos]) => ({ description, items: itemNos })),
        { origin: 'testify' },
      );
    }
    created.push({ quizId: quiz.id, title: subject.title, items, competencies: competencyItems.size });
  }

  return {
    examId,
    examTitle: exportResp.exam.title ?? validate.exam.title ?? '',
    examCode,
    schoolName: exportResp.school.school_name,
    created,
    incompatible,
    notSelected,
    requiredCustomFields: customFields,
  };
}
